import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  Button,
  Label,
  RadioGroup,
  RadioGroupItem,
  Textarea,
} from "@jem-open/jem-ui";
import { ShieldCheck, Paperclip } from "lucide-react";
import { AttendanceException } from "../services/model/AttendanceException";
import {
  formatExceptionTime,
  getExceptionShiftLabel,
  getShiftTimeRange,
} from "../utils/exceptionDisplayUtils";
import { useGetSubmissionDetail } from "../services/api/SupervisorClockSubmissionApiService";

interface SupervisorClockActionModalProps {
  exception: AttendanceException;
  open: boolean;
  onClose: () => void;
  onResolve: (id: string, action: string, reason?: string) => void;
}

const SupervisorClockActionModal: React.FC<SupervisorClockActionModalProps> = ({
  exception,
  open,
  onClose,
  onResolve,
}) => {
  const [choice, setChoice] = useState<"approve" | "reject" | "">("");
  const [rejectReason, setRejectReason] = useState("");

  // The synthetic supervisor exception carries the submission id (not the photo
  // URL) in `details`. Fetch the submission detail — which signs the photo URL
  // server-side — only while the modal is open, mirroring SupervisorClockReviewModal.
  const submissionId =
    typeof exception.details?.submission_id === "string"
      ? exception.details.submission_id
      : null;
  const {
    data: submission,
    isLoading: isSubmissionLoading,
    error: submissionError,
  } = useGetSubmissionDetail(open ? submissionId : null);
  const photoUrl = submission?.verification_photo_url ?? null;
  // The synthetic exception synchronously tells us whether a photo exists via
  // `details.verification_photo_path` — gate the skeleton on it so we don't
  // flash a loading photo for submissions that have none.
  const hasPhoto =
    typeof exception.details?.verification_photo_path === "string" &&
    exception.details.verification_photo_path.length > 0;
  const clockedInEmployees = submission?.employees ?? [];
  // The synthetic exception stores the supervisor's name in `employee_name`
  // (and `details.supervisor_name`); the real clocked-in employees come from
  // the fetched submission.
  const supervisorName =
    (typeof exception.details?.supervisor_name === "string" &&
      exception.details.supervisor_name) ||
    exception.employee_name;

  useEffect(() => {
    if (open) {
      setChoice("");
      setRejectReason("");
    }
  }, [exception.id, open]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-xs">
            <ShieldCheck size={18} className="text-amber-500" />
            Supervisor Clock-In — Review
          </DialogTitle>
          <DialogDescription>
            Review the clock-in captured by supervisor {supervisorName}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-md py-sm">
          {hasPhoto && isSubmissionLoading && (
            <div className="rounded-lg overflow-hidden border border-greyscale-border bg-neutral-50">
              <div className="w-full h-[240px] animate-pulse bg-neutral-100" />
            </div>
          )}
          {hasPhoto && !isSubmissionLoading && photoUrl && (
            <div className="rounded-lg overflow-hidden border border-greyscale-border bg-neutral-50">
              <img
                src={photoUrl}
                alt={`Clock-in verification captured by supervisor ${supervisorName}`}
                className="w-full h-[240px] object-cover"
              />
              <div className="flex items-center justify-between px-sm py-xs text-xs text-greyscale-text-caption">
                <span className="flex items-center gap-xxs">
                  <Paperclip size={12} />
                  supervisor-clock-in-{submissionId}.jpg
                </span>
                <span>
                  Uploaded by supervisor at{" "}
                  {formatExceptionTime(exception.timestamp)}
                </span>
              </div>
            </div>
          )}

          <div className="rounded-lg border border-greyscale-border p-sm space-y-xxs">
            <div className="text-sm">
              <span className="font-semibold">
                {clockedInEmployees.length === 1 ? "Employee:" : "Employees:"}
              </span>{" "}
              {(() => {
                if (clockedInEmployees.length > 0) {
                  return (
                    <ul className="mt-xxs list-disc space-y-xxxs pl-md">
                      {clockedInEmployees.map((emp) => (
                        <li key={emp.id}>{emp.employee_name}</li>
                      ))}
                    </ul>
                  );
                }
                if (isSubmissionLoading) {
                  return (
                    <span className="text-greyscale-text-caption">
                      Loading…
                    </span>
                  );
                }
                if (submissionError) {
                  return (
                    <span className="text-red-600">
                      Failed to load the clock-in details. Close and try again.
                    </span>
                  );
                }
                return <span className="text-greyscale-text-caption">—</span>;
              })()}
            </div>
            <p className="text-sm">
              <span className="font-semibold">Site:</span>{" "}
              {exception.location_name} — {exception.region_name}
            </p>
            <p className="text-sm">
              <span className="font-semibold">Shift:</span>{" "}
              {exception.shift_start && exception.shift_end
                ? `${getExceptionShiftLabel(exception)} (${getShiftTimeRange(
                    exception.shift_start,
                    exception.shift_end
                  )})`
                : "Supervisor clock-in"}
            </p>
            <p className="text-sm">
              <span className="font-semibold">Clock time:</span>{" "}
              {formatExceptionTime(exception.timestamp)}
            </p>
            <p className="text-sm text-greyscale-text-caption">
              Clocked in by Supervisor: {supervisorName}.
            </p>
          </div>

          <div className="space-y-xs">
            <Label className="text-sm font-semibold">Decision</Label>
            <RadioGroup
              value={choice}
              onValueChange={(v) => setChoice(v as "approve" | "reject")}
            >
              <div className="flex items-center gap-xs">
                <RadioGroupItem value="approve" id="sup-approve" />
                <Label htmlFor="sup-approve" className="text-sm">
                  Approve clock-in
                </Label>
              </div>
              <div className="flex items-center gap-xs">
                <RadioGroupItem value="reject" id="sup-reject" />
                <Label htmlFor="sup-reject" className="text-sm">
                  Reject clock-in
                </Label>
              </div>
            </RadioGroup>
          </div>

          {choice === "reject" && (
            <div className="space-y-xxs">
              <Label className="text-sm font-semibold">
                Reason for rejection
              </Label>
              <Textarea
                placeholder="Provide a reason for rejecting this clock-in..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="min-h-[80px]"
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button
            disabled={
              !choice ||
              (choice === "reject" && !rejectReason.trim()) ||
              Boolean(submissionError)
            }
            variant={choice === "reject" ? "destructive" : "default"}
            onClick={() => {
              onResolve(
                exception.id,
                choice,
                choice === "reject" ? rejectReason : undefined
              );
              onClose();
            }}
          >
            {(() => {
              if (choice === "approve") return "Approve";
              if (choice === "reject") return "Reject";
              return "Confirm";
            })()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SupervisorClockActionModal;
